import { useEffect, useRef, type ReactNode } from 'react';
import { PenLine, ChevronRight, Sparkles } from '../../icons';
import './RightRail.css';

/**
 * ⑤ 우측 사이드 레일 표준 껍데기 — 설계 §5-⑤.
 * docs/design/design-20260717-common-layout-skeleton.md
 *
 * 구성(위→아래): 프로필 슬롯 → 메모 버튼 → 구분선 → children(멤버/즐겨찾기 등) → 접기(»).
 * 세션 시계는 2026-08-11 에 하단 상태바 우측으로 이관됐다(레일에서는 제거).
 * **opt-in slot**(전역 상시 아님) — 페이지가 LayoutShell.rightRail 에 넘길 때만 렌더된다.
 * ⚠ 채팅은 기존 ChatParticipantPanel(멤버 레일)과 중복되므로 opt-in 하지 않거나 이관해야 한다(§5-⑤).
 */
export interface RightRailProps {
  /** 상단 프로필 아바타 등 (선택). */
  profile?: ReactNode;
  /** 메모 버튼 핸들러 (선택). 미제공 시 버튼 미표시. */
  onMemo?: () => void;
  /**
   * 메모 버튼을 자리만 잡고 비활성으로 렌더 (선택, additive).
   * 앱에 메모 기능이 아직 없는 화면에서 레이아웃 파리티를 맞추되, 눌리는 척하지 않기 위한 것.
   * true 면 onMemo 없이도 버튼이 렌더되고 disabled 로 나간다.
   */
  memoDisabled?: boolean;
  /** 메모 버튼 title (선택) — 비활성 사유 안내 등. */
  memoTitle?: string;
  /**
   * AI 패널 토글 (선택, additive — 2026-09-09 · 설계 판 A · 정의서 E2).
   * 미제공 시 버튼 미표시 — `onMemo` 와 **같은 모양**의 계약이라 기존 소비처는 회귀 0 이다.
   */
  onAi?: () => void;
  /** AI 버튼이 지금 켜져 있는가 — 레일에서 **토글 상태로 계속 보여야** 한다(정의서 P2). */
  aiActive?: boolean;
  /**
   * (제거됨 2026-08-11) 프로필 아래 세션 시계는 하단 상태바 우측으로 옮겼다.
   * 레일은 프로필·메모·본문만 담당한다. 세션 만료 처리는 AppLayout 의 SessionIdleGuard 가 상시 담당.
   */
  /** 접기(») 핸들러 (선택). */
  onCollapse?: () => void;
  /** 접기 버튼 aria-label (선택). 미지정 시 기존 문구 유지. */
  collapseLabel?: string;
  /** 멤버 스택·즐겨찾기 등 본문 (선택). */
  children?: ReactNode;
  /**
   * 본문 **아래**, 접기(») **위**에 붙는 고정 자리 (선택, additive — 2026-08-18).
   * 즐겨찾기처럼 "레일 제일 하단" 이 자리인 항목을 위한 슬롯이다. children 은 스크롤되는
   * 본문이라 항목이 늘면 아래로 밀려나는데, 이 슬롯은 본문 길이와 무관하게 항상 바닥에 남는다.
   * 미지정이면 종전과 완전히 동일한 마크업(회귀 0).
   */
  footer?: ReactNode;
  /**
   * 자기 폭을 전역 `--app-rightrail-w` 로 알릴지 (기본 true — 기존 동작).
   * 전역 호스트(RightRailHost)처럼 **접기 애니메이션과 동기해** 변수를 직접 소유하는 소비처는
   * false 로 끈다. 접히는 중에는 실측 폭(=슬라이드 중간값)이 아니라 목표값이 필요하기 때문.
   */
  measureWidthVar?: boolean;
}

export function RightRail({
  profile, onMemo, memoDisabled, memoTitle, onAi, aiActive, onCollapse, collapseLabel,
  measureWidthVar = true, children, footer,
}: RightRailProps) {
  const showMemo = Boolean(onMemo) || Boolean(memoDisabled);
  const showAi = Boolean(onAi);
  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * 레일이 떠 있는 동안 자기 폭을 전역 CSS 변수 `--app-rightrail-w` 로 알린다.
   * 메모 패널(전역 소유)이 이 값만큼 오른쪽에서 떨어져 붙어 **레일을 덮지 않는다**
   * (design-20260724 §4.1 — 레일의 메모 버튼이 토글 상태로 계속 보여야 한다).
   * 레일이 없는 화면에서는 변수가 지워져 패널이 화면 우측 끝에 붙는다.
   */
  useEffect(() => {
    // 전역 호스트가 접기 애니메이션과 동기해 변수를 소유하는 경우엔 실측을 끈다(§ measureWidthVar).
    if (!measureWidthVar) return;
    const el = rootRef.current;
    if (!el) return;
    const apply = () => {
      const w = el.getBoundingClientRect().width;
      document.documentElement.style.setProperty('--app-rightrail-w', `${Math.round(w)}px`);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => {
      window.removeEventListener('resize', apply);
      document.documentElement.style.removeProperty('--app-rightrail-w');
    };
  }, [measureWidthVar]);

  return (
    <div className="right-rail" ref={rootRef}>
      {profile != null && (
        <div className="right-rail__profile">{profile}</div>
      )}

      {/* 프로필 ↔ 메모 구분선 — 즐겨찾기와 같은 방식으로 그룹을 나눈다(2026-07-24 사용자 요청). */}
      {profile != null && (showMemo || showAi) && <div className="right-rail__divider" />}

      {showMemo && (
        // 메모 그룹 — 즐겨찾기 그룹(라벨 + 사각 버튼)과 같은 구성·같은 톤.
        // 종전에는 primary 색 테두리·글자라 레일에서 혼자 튀었다(2026-07-24 사용자 지적).
        <div className="right-rail__memo-group">
          <div className="right-rail__memo-label">메모</div>
          <button
            type="button"
            className="right-rail__memo"
            onClick={onMemo}
            disabled={memoDisabled}
            title={memoTitle}
            aria-label="메모"
          >
            <PenLine size={15} aria-hidden />
          </button>
        </div>
      )}

      {/*
        AI 칸 — 메모 옆이다(정의서 E2). 메모 그룹과 **같은 구성·같은 톤**이라 레일에서 혼자 튀지
        않는다. 패널은 ⑤ 레일을 덮지 않으므로(P2) 이 버튼은 열려 있는 동안에도 계속 보이고,
        `aria-pressed` 로 **토글 상태**를 말한다.
      */}
      {showAi && (
        <div className="right-rail__memo-group">
          <div className="right-rail__memo-label">AI</div>
          <button
            type="button"
            className="right-rail__memo"
            onClick={onAi}
            aria-pressed={aiActive ?? false}
            title="AI"
            aria-label="AI"
          >
            <Sparkles size={15} aria-hidden />
          </button>
        </div>
      )}

      {(profile != null || showMemo || showAi) && <div className="right-rail__divider" />}

      {children != null && <div className="right-rail__body scrollbar-thin">{children}</div>}

      {/* 하단 고정 슬롯 — 본문과 구분선으로 나눈다(프로필↔메모와 같은 방식). */}
      {footer != null && (
        <>
          <div className="right-rail__divider" />
          <div className="right-rail__footer">{footer}</div>
        </>
      )}

      {onCollapse && (
        <button
          type="button"
          className="right-rail__collapse"
          onClick={onCollapse}
          aria-label={collapseLabel ?? '우측 사이드 접기'}
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      )}
    </div>
  );
}
